/**
 * Один контейнер повідомлень у порталі (UX-13): зона тостів і живий регіон оголошень. Обидва
 * регіони існують завжди: регіон, вставлений разом з текстом, програми читання пропускають.
 *
 * Тост: внизу ліворуч; видно не більше двох — поточне підтвердження над помилкою, що висить.
 * Підтвердження — 5 с з паузою при наведенні й фокусі, у кожного тосту свій таймер і своя пауза;
 * помилка висить до закриття. Регіони — дві постійні сусідні обгортки (`status` і `alert`), без
 * вкладення одного в інший. Фокус у тост не перестрибує; `F8` веде до найновішого тосту, `Esc`
 * закриває його, і фокус повертається туди, де був. Хост лишається в першому екрані, а не в лінивому чанку: тост
 * помилки має з'явитись і без мережі, коли чанк уже не завантажити.
 */
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { useHotkeys } from '../../app/hotkeys.ts';
import { GLOBAL } from '../../app/keymap.ts';
import { common as c } from '../../i18n/uk/common.ts';
import { keys as k } from '../../i18n/uk/keys.ts';
import { states as t } from '../../i18n/uk/states.ts';
import { Icon } from '../Icon.tsx';
import {
  dismissToast,
  getAnnouncement,
  getToasts,
  rememberToastReturn,
  subscribe,
  TOAST_MS,
  type ToastItem,
  takeToastReturn,
  visibleToasts,
} from './store.ts';

export function NotifyHost() {
  const toasts = useSyncExternalStore(subscribe, getToasts, getToasts);
  const announcement = useSyncExternalStore(subscribe, getAnnouncement, getAnnouncement);
  const { status, alert } = visibleToasts(toasts);
  const regionRef = useRef<HTMLElement>(null);
  useHotkeys(
    [
      {
        ...GLOBAL.toasts,
        label: k.keyToasts,
        handler: () => {
          // Зони по колу (states.md, розд. 9): тости дій від найновішого, потім інші зони тостів
          // застосунку — елементи з нейтральною ознакою `data-toast-zone` (web-shell.md, розд. 5).
          // Наступне натискання — до наступної зони, а не знову в ту саму.
          const actions = [status, alert]
            .filter((x): x is ToastItem => Boolean(x))
            .sort((a, b) => b.at - a.at)
            .map((t) => regionRef.current?.querySelector<HTMLElement>(`[data-toast-id="${t.id}"]`));
          const zones = [
            ...actions,
            ...document.querySelectorAll<HTMLElement>('[data-toast-zone]'),
          ].filter((z): z is HTMLElement => Boolean(z));
          if (zones.length === 0) return;
          const at = zones.findIndex((z) => z.contains(document.activeElement));
          const zone = zones[(at + 1) % zones.length] as HTMLElement;
          const target = zone.querySelector<HTMLElement>('a, button');
          if (!target) return;
          if (at < 0) rememberToastReturn(document.activeElement);
          target.focus();
        },
      },
    ],
    { scope: 'global' },
  );

  const dismiss = useCallback((id: number) => {
    const hadFocus = regionRef.current?.contains(document.activeElement);
    dismissToast(id);
    // Фокус був у тості, що зник: назад до елемента, з якого прийшли, інакше — до вмісту.
    if (hadFocus) takeToastReturn()?.focus();
  }, []);

  return createPortal(
    <>
      <section
        ref={regionRef}
        className="toast-region"
        aria-label={`${t.notifyRegion} (${t.notifyRegionKeys})`}
      >
        <div className="toast-region__slot" role="status" aria-live="polite">
          {status && <Toast key={status.id} item={status} onDismiss={dismiss} />}
        </div>
        <div className="toast-region__slot" role="alert">
          {alert && <Toast key={alert.id} item={alert} onDismiss={dismiss} />}
        </div>
      </section>
      <div className="sr-only" role="status" aria-live="polite" data-testid="announce-polite">
        {announcement && !announcement.assertive && (
          <span key={announcement.n}>{announcement.text}</span>
        )}
      </div>
      <div className="sr-only" aria-live="assertive" data-testid="announce-assertive">
        {announcement?.assertive && <span key={announcement.n}>{announcement.text}</span>}
      </div>
    </>,
    document.body,
  );
}

function Toast({ item, onDismiss }: { item: ToastItem; onDismiss: (id: number) => void }) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const isAlert = item.tone === 'alert';
  const paused = hovered || focused;
  const countdown = item.countdown;
  const [now, setNow] = useState(Date.now);
  const left = countdown ? Math.max(0, Math.ceil((countdown.until - now) / 1000)) : 0;
  // Програмі читання — лише початок очікування: щосекундний текст поза живим регіоном (UX-19).
  // Нове очікування, злите в цей тост, — новий початок; секунди, що йдуть, його не міняють.
  const spoken = useMemo(
    () => (countdown ? countdown.text(Math.ceil((countdown.until - Date.now()) / 1000)) : ''),
    [countdown],
  );

  // `version` у залежностях: злитий тост показується заново, повні 5 с.
  // biome-ignore lint/correctness/useExhaustiveDependencies: version — навмисний тригер перезапуску
  useEffect(() => {
    if (paused || isAlert || countdown) return;
    const timer = setTimeout(() => onDismiss(item.id), TOAST_MS);
    return () => clearTimeout(timer);
  }, [paused, isAlert, countdown, item.id, item.version, onDismiss]);

  // Тік — на межі цілої секунди до кінця: «0» з'являється вчасно, а не до секунди пізніше.
  useEffect(() => {
    const remaining = countdown ? countdown.until - now : 0;
    if (remaining <= 0) return;
    const timer = setTimeout(() => setNow(Date.now()), remaining % 1000 || 1000);
    return () => clearTimeout(timer);
  }, [countdown, now]);

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: пауза при наведенні й фокусі та Esc — делегування з кнопок тосту (UX-13)
    <div
      className="toast"
      data-tone={item.tone}
      data-toast-id={item.id}
      data-testid="toast"
      data-count={item.count}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocused(false);
      }}
      onKeyDown={(e) => {
        if (e.key !== 'Escape') return;
        // Глобальні клавіші (`useHotkeys`) пропускають оброблену подію: Esc у тості не веде зі сторінки.
        e.preventDefault();
        e.stopPropagation();
        onDismiss(item.id);
      }}
    >
      {isAlert ? <Icon name="alert-circle" /> : <Icon name="check" />}
      <span className="toast__text">
        {item.text}
        {countdown && (
          <>
            {' '}
            <span aria-hidden="true" data-testid="toast-countdown">
              {countdown.text(left)}
            </span>
            <span className="sr-only">{spoken}</span>
          </>
        )}
      </span>
      {item.action && (
        <button
          className="toast__action"
          type="button"
          onClick={() => {
            item.action?.onAction();
            onDismiss(item.id);
          }}
        >
          {item.action.label}
        </button>
      )}
      <button
        className="toast__action toast__close"
        type="button"
        aria-label={c.close}
        onClick={() => onDismiss(item.id)}
      >
        <Icon name="close" />
      </button>
    </div>
  );
}
